// Global Types
import type {
  $FullAdminRegatta,
  $FullRegattaDay,
  $FullRegattaProtestEnd,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  RegattaProtestType,
} from 'common.data';
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import momentTimezone from 'moment-timezone';
import {
  cleanFormValues,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  formatTime,
  getDateRange,
  getRegattaEntryTitle,
  getRegionByRegionSeasonId,
} from 'common.helpers/dist/browser';
import {
  i18next,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import RegattaProtestFields from '../../../fields/RegattaProtestFields';
import RegattaProtestEndFields from '../../../fields/RegattaProtestEndFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Types
import type {
  $ReducerState,
} from '../../../../redux';

export type $OwnProps = $Translate & {
  id: string;
  item: $FullAdminRegatta;
  timezone: string;
};

export type $StateProps = {
  main: $ReducerState<'main'>;
};

export type $DispatchProps = Record<string, unknown>;

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  data: Array<$Model<'RegattaProtest'>>;
  loading: boolean;
  resourceDataRegattaDay: Array<$FullRegattaDay>;
  resourceDataRegattaResultTable: Array<$Model<'RegattaResultTable'>>;
};

const dateFormat: string = 'YYYY-MM-DD';

class RegattaProtestsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
      resourceDataRegattaDay: [],
      resourceDataRegattaResultTable: [],
    };
  }

  componentDidMount(): void {
    this.getData();
    this.getResourceDataRegattaDay();
    this.getResourceDataRegattaResultTable();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
      this.getResourceDataRegattaDay();
      this.getResourceDataRegattaResultTable();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaProtest') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });

    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId
          && (data.modelName === 'RegattaDay' || data.modelName === 'RegattaProtestEnd')
        ) {
          this.getResourceDataRegattaDay();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });

    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaResultTable') {
          this.getResourceDataRegattaResultTable();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaProtest({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getResourceDataRegattaDay(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const resourceDataRegattaDay = await Services.Api.getResourceRegattaDay({
          RegattaId,
        });

        await this.setStateAsync({
          loading: false,
          resourceDataRegattaDay,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getResourceDataRegattaResultTable(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const resourceDataRegattaResultTable = await Services.Api.getResourceRegattaResultTable({
          RegattaId,
        });

        await this.setStateAsync({
          loading: false,
          resourceDataRegattaResultTable,
        });
      },
      errorAction: async () => this.stopLoader(),
    });
  }

  async getRegattaEntryOptions(search: string): Promise<Array<{ id: string; Name: string }>> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    const foundRegattaEntries = await Services.Api.findManyRegattaEntry({
      RegattaId,
      search,
    });

    return foundRegattaEntries.map((foundItem) => ({
      id: foundItem.id,
      Name: getRegattaEntryTitle({
        bowNoEnabled: item.BowNoEnabled,
        countryEnabled: item.CountryEnabled,
        regattaEntry: foundItem,
      }),
    }));
  }

  async stopLoader(): Promise<void> {
    await this.setStateAsync({
      loading: false,
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    return (
      <SectionHeader
        bottomBorder
        description={'A list of regatta protests that is published on the regatta page'}
        iconClass={'icon-hourglass'}
        rightButtons={(
          <Button
            color={'secondary'}
            href={getDocumentUrl({
              cdnUrl: config.CDN_URL,
              docRoute: docRoute.adminRegattaProtests,
              docType: docType.pdf,
              id: item.id,
              language: i18next.language as $Language,
            })}
            id={'regatta-protests-list-button'}
            outline
            size={'sm'}
            target={'_blank'}
          >
            <em className={'far fa-file-pdf mr-1'} />
            {t('Protests list')}
          </Button>
        )}
        title={t('Regatta protests')}
      />
    );
  }

  renderTableItem(dataItem: $Model<'RegattaProtest'>) {
    const {
      t,
      timezone,
    } = this.props;
    const {
      resourceDataRegattaDay,
    } = this.state;

    const {
      id,
    } = dataItem;

    return (
      <tr key={id}>
        <td>{`#${dataItem.CaseNo || t('TBD')}`}</td>
        <td className={'text-center'}>{dataItem.RaceNo}</td>
        <td>{dataItem.Protestor}</td>
        <td>{dataItem.Protestee}</td>
        <TdDate
          value={dataItem.HearingDate}
        />
        <td>
          <div className={'d-flex justify-content-end'}>
            <Button
              className={'mr-1'}
              color={'secondary'}
              href={getDocumentUrl({
                cdnUrl: config.CDN_URL,
                docRoute: docRoute.adminRegattaProtest,
                docType: docType.pdf,
                id,
                language: i18next.language as $Language,
              })}
              id={`regatta-protest-file-button-${id}`}
              outline
              size={'sm'}
              target={'_blank'}
            >
              <em className={'far fa-file-pdf'} />
            </Button>
            <FormDialog
              fields={(
                <RegattaProtestFields
                  getters={{
                    getRegattaEntryOptions: async (
                      search: string,
                    ) => this.getRegattaEntryOptions(search),
                  }}
                  item={dataItem}
                  range={getDateRange({
                    data: resourceDataRegattaDay.map(({
                      NameDate,
                    }) => NameDate),
                    extents: true,
                    timezone,
                  })}
                />
              )}
              id={`update-regatta-protest-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'RegattaProtest'>>({
                    ...dataItem,
                    ...values,
                  });

                  await Services.Api.upsertRegattaProtest(params);
                },
              })}
              size={'lg'}
              subTitle={`#${dataItem.CaseNo || t('Undefined')}`}
              title={t('Regatta protest')}
            >
              <Button
                className={'mr-1'}
                color={'warning'}
                id={`update-regatta-protest-button-${id}`}
                outline
                size={'sm'}
              >
                {t('Edit')}
              </Button>
            </FormDialog>
            <DeleteDialog
              content={t('Do you really want to delete?')}
              id={`delete-regatta-protest-dialog-${id}`}
              labelCloseButton={t('Close')}
              labelDeleteButton={t('Delete')}
              onAction={() => defaultDeleteItem({
                action: async () => Services.Api.deleteRegattaProtest(dataItem),
              })}
              subTitle={`#${dataItem.CaseNo || t('Undefined')}`}
              title={t('Regatta protest')}
            >
              <Button
                color={'danger'}
                id={`delete-regatta-protest-button-${id}`}
                outline
                size={'sm'}
              >
                {t('Delete')}
              </Button>
            </DeleteDialog>
          </div>
        </td>
      </tr>
    );
  }

  renderTable(filteredData: Array<$Model<'RegattaProtest'>>) {
    const {
      t,
    } = this.props;

    return (
      <>
        <tr>
          <th className={'py-1'}>{t('Case no.')}</th>
          <th className={'text-center py-1'}>{t('Race')}</th>
          <th className={'py-1'}>{t('Protestor')}</th>
          <th className={'py-1'}>{t('Protestee')}</th>
          <th className={'py-1'}>{t('Hearing date')}</th>
          <ThQty
            className={'py-1'}
            value={filteredData.length}
          />
        </tr>
        {filteredData.map((dataItem: $Model<'RegattaProtest'>) => this.renderTableItem(dataItem))}
      </>
    );
  }

  renderRegattaDayProtestEnds(regattaDay: $FullRegattaDay, regattaDayMoment: moment.Moment) {
    const {
      item,
      main,
      t,
    } = this.props;
    const {
      resourceDataRegattaResultTable,
    } = this.state;

    const region = getRegionByRegionSeasonId({
      regions: main.regions,
      RegionSeasonId: item.RegionSeasonId,
    });

    const RegattaDayId = regattaDay.id;

    const newItem: $ModelUpsertParams<'RegattaProtestEnd'> = {
      EndDate: regattaDayMoment.toDate(),
      RegattaDayId,
      RegattaResultTableId: '',
    };

    return (
      <tr>
        <td colSpan={6}>
          <div>
            {regattaDay.RegattaProtestEnds.map((dataItem: $FullRegattaProtestEnd) => {
              const {
                id,
              } = dataItem;

              return (
                <div
                  className={'d-inline-block mb-1 mr-1'}
                  key={id}
                >
                  <div className={'input-group input-group-sm'}>
                    <div className={'form-control'}>
                      {`${dataItem.RegattaResultTable.Name}: `}
                      <span className={'text-bold'}>
                        {formatTime({
                          timeFormat: region.TimeFormat,
                          value: dataItem.EndDate,
                        })}
                      </span>
                    </div>
                    <DeleteDialog
                      content={t('Do you really want to delete?')}
                      id={`delete-regatta-protest-end-dialog-${id}`}
                      labelCloseButton={t('Close')}
                      labelDeleteButton={t('Delete')}
                      onAction={() => defaultDeleteItem({
                        action: async () => Services.Api.deleteRegattaProtestEnd(dataItem),
                      })}
                      subTitle={''}
                      title={t('Regatta protest time limit')}
                    >
                      <Button
                        color={'danger'}
                        id={`delete-regatta-protest-end-button-${id}`}
                        outline
                        size={'sm'}
                      >
                        {t('Delete')}
                      </Button>
                    </DeleteDialog>
                  </div>
                </div>
              );
            })}
            <FormDialog
              fields={(
                <RegattaProtestEndFields
                  date={regattaDay.NameDate}
                  item={newItem}
                  resourceData={resourceDataRegattaResultTable}
                />
              )}
              id={`create-regatta-protest-end-dialog-${RegattaDayId}`}
              labelCloseButton={t('Close')}
              labelSaveButton={t('Save')}
              onAction={(values) => defaultUpsertItem({
                action: async () => {
                  const params = cleanFormValues<$Model<'RegattaProtestEnd'>>({
                    ...newItem,
                    ...values,
                  });

                  await Services.Api.upsertRegattaProtestEnd(params);
                },
              })}
              size={'sm'}
              subTitle={t('New')}
              title={t('Regatta day protest time limit')}
            >
              <Button
                className={'d-inline-block mb-1'}
                color={'info'}
                id={`create-regatta-protest-end-button-${RegattaDayId}`}
                size={'sm'}
              >
                {t('Add day protest time limit')}
              </Button>
            </FormDialog>
          </div>
        </td>
      </tr>
    );
  }

  renderRegattaDay(regattaDay: $FullRegattaDay, regattaDayMoment: moment.Moment, index: number) {
    const {
      item,
      t,
      timezone,
    } = this.props;

    const {
      resourceDataRegattaDay,
    } = this.state;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaProtest'> = {
      AltInfo: false,
      AltInfoContent: null,
      CaseNo: null,
      Content: null,
      Decision: null,
      Drawing: null,
      File: null,
      Flag: false,
      FlagContent: null,
      Hailing: false,
      HailingContent: null,
      HearingDate: null,
      Image: null,
      PreContent: null,
      Protestee: '',
      ProtesteeGroup: null,
      ProtesteeRegattaEntryId: null,
      Protestor: '',
      ProtestorGroup: null,
      ProtestorRegattaEntryId: null,
      RaceDate: regattaDay.NameDate,
      RaceNo: null,
      RegattaId,
      Rule: null,
      Type: RegattaProtestType.type1BoatToBoat,
      Witnesses: null,
    };

    const range = getDateRange({
      data: resourceDataRegattaDay.map(({
        NameDate,
      }) => NameDate),
      extents: true,
      timezone,
    });

    const weekDay: string = regattaDayMoment.format('ddd');

    return (
      <tr className={'bg-hightlight'}>
        <td className={'py-2'}>
          <div className={'d-flex justify-content-start align-items-center'}>
            <div className={'h2 mb-0 mr-2'}>
              {index + 1}
            </div>
            <div>
              <h5 className={'mb-0'}>
                {` ${t('Regatta day')}`}
              </h5>
              <small className={'d-block text-muted lh-1 mt-1'}>
                <span className={'text-bold'}>{weekDay}</span>
                <span>{` | ${regattaDay.NameDate}`}</span>
              </small>
            </div>
          </div>
        </td>
        <td
          className={'text-right py-2'}
          colSpan={5}
        >
          <FormDialog
            fields={(
              <RegattaProtestFields
                getters={{
                  getRegattaEntryOptions: async (
                    search: string,
                  ) => this.getRegattaEntryOptions(search),
                }}
                item={newItem}
                range={range}
              />
            )}
            id={'create-regatta-protest-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaProtest'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaProtest(params);
              },
            })}
            size={'lg'}
            subTitle={t('New')}
            title={t('Regatta protest')}
          >
            <Button
              color={'info'}
              id={'create-regatta-protest-button'}
              size={'sm'}
              title={t('Add new regatta protest')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        </td>
      </tr>
    );
  }

  renderRegattaDays() {
    const {
      timezone,
    } = this.props;
    const {
      data,
      resourceDataRegattaDay,
    } = this.state;

    return resourceDataRegattaDay.map((
      regattaDay: $FullRegattaDay,
      index: number,
    ) => {
      const regattaDayMoment = momentTimezone.tz(
        regattaDay.NameDate,
        dateFormat,
        timezone,
      );

      const filteredData: Array<$Model<'RegattaProtest'>> = data.filter((
        dataItem: $Model<'RegattaProtest'>,
      ) => regattaDayMoment.isSame(
        dataItem.RaceDate,
        'day',
      ));

      return (
        <React.Fragment key={regattaDay.NameDate}>
          <tbody>
            <tr>
              <td
                className={'border-0 p-1'}
                colSpan={6}
                style={{
                  backgroundColor: 'rgba(200,200,200,0.2)',
                }}
              />
            </tr>
          </tbody>
          <tbody>
            {this.renderRegattaDay(
              regattaDay,
              regattaDayMoment,
              index,
            )}
          </tbody>
          <tbody>
            {filteredData.length > 0 && this.renderTable(
              filteredData,
            )}
          </tbody>
          <tbody>
            {this.renderRegattaDayProtestEnds(
              regattaDay,
              regattaDayMoment,
            )}
          </tbody>
        </React.Fragment>
      );
    });
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        <Table responsive>
          {this.renderRegattaDays()}
        </Table>
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaProtestsSectionView;
